/**
 * `/segments` — every segment in this workspace.
 *
 * The counts are on the list, not hidden behind a click. That is deliberate:
 * this is where someone forms an expectation about how big a send will be, and
 * a list that showed only "1,287" would set that expectation wrong before they
 * ever opened the segment. Both people-numbers travel together everywhere —
 * rows in the union, and the de-duplicated recipients a campaign would actually
 * message — and the channel reach travels beside them, because a campaign picks
 * one channel and the segment total is not the reach.
 *
 * Nothing here is cached. Every row's counts were computed by the API on this
 * request, so re-importing one of the lists behind a segment changes what this
 * screen says the next time it loads. That is the correct behaviour and the
 * reason a segment copies no rows.
 *
 * **The legend under the table is load-bearing and was wrong.** Four numbers in
 * two columns are not self-explanatory, and the sentence explaining the Reach
 * column described it as a count of rows. It is a count of distinct addresses.
 * See `CountsLegend` below for what that mis-description costs; the short
 * version is that it is the slice 2 defect, restated in prose after the query
 * behind it had been fixed. The rest of this screen's copy got shorter in this
 * slice. This clause got *corrected*, and it stays on the page rather than
 * moving into a tooltip, because it is the difference between a number a
 * campaign can deliver and a number it cannot.
 *
 * **Slice 50: two kinds of row now live in this table, and one of them has to
 * say so.** A cohort holds the people one Overview count counted, on one day; it
 * combines no uploads at all, so its Lists cell is the same lowercase "none" a
 * static segment gets when somebody has not filled it yet. Those two zeros are
 * both real and they mean opposite things — one is *nothing to see*, the other is
 * *that is not what this segment is*. The `Snapshot` chip beside the name is what
 * separates them, and the provenance under it — which count, and the day it was
 * counted — is the fact a person about to send actually needs, because a cohort
 * ages and its name need not say when it was taken. Three labels, no sentence,
 * and all three from `lib/segment-display.ts` so this screen and the detail
 * screen cannot describe one segment two ways.
 *
 * **Slice 66: the Reachable cell is two small bars, and still two numbers.**
 * `ChannelReach` draws the Blueprint's by-source rows now — Email and WhatsApp,
 * each "N of M" where M is the row's unique recipients, the same comparison
 * the detail screen draws — so a channel that reaches half the segment looks
 * like half at a glance. The column widened from 9rem to 15rem to hold the
 * "of" without truncating the count, and the table's minimum width grew to
 * match; it scrolls sideways on a phone as it always did, rather than
 * squeezing a send size into an ellipsis. The rows are counts per channel and
 * are never added together: one person can be on both. Counts in this table
 * are sans with tabular figures rather than mono, whose dotted zero reads as
 * an 8 at this size.
 *
 * **Slice 67: a third kind, the rule segment, wears a "Dynamic" chip** in the
 * brand's violet — the pillar colour, not a status — and its Lists cell says
 * "rules" rather than "none", for the same reason the cohort chip exists: a
 * segment whose members are defined by rules has no lists, and "none" would
 * read as somebody forgetting to fill it.
 */
import { DEFAULT_PAGE_SIZE, segmentSortSchema, type SegmentSummary } from '@wend/shared';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  Input,
  Spinner,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import { ListFilter, Plus, Search, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { ChannelReach, ContactsAndRecipients } from '../../components/SegmentCounts';
import { errorMessage } from '../../lib/api/errors';
import { listSegments } from '../../lib/api/segments';
import { formatDate, pluralise } from '../../lib/format';
import { cohortProvenance } from '../../lib/segment-display';
import { useAsyncData } from '../../lib/use-async-data';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

export function SegmentsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const sortField = segmentSortSchema.catch('createdAt').parse(sort.key);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listSegments(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [page, search, sortField, sort.direction],
  );

  const columns = useMemo<Array<DataTableColumn<SegmentSummary>>>(
    () => [
      {
        key: 'name',
        header: 'Segment',
        sortable: true,
        cell: (segment) => {
          /*
            THE CHIP IS WHAT STOPS THE LISTS COLUMN READING AS A DEFECT.

            A cohort combines no uploads, so its Lists cell is the mono "none"
            below — a real zero, and one that says "somebody forgot to put lists
            in this one" to anybody who does not already know what kind of
            segment they are looking at. `Snapshot` beside the name is the fact
            that makes the zero legible, and the two labels under it are the
            provenance: which count produced this population, and the day it was
            counted. All three come from `lib/segment-display.ts`, because the
            detail screen draws exactly the same three.

            A static segment gets nothing extra. The absence of the chip is the
            other state — slice 29 deleted a badge that distinguished a segment
            from nothing, and a badge on every row is not a badge.

            SLICE 66 MOVED THE CHIP OFF NEUTRAL GREY, ONTO AN IDENTITY COLOUR.
            Neutral is the tone of "nothing to report"; a kind is something to
            report. `ident-a` is the identity slot for the static-list and
            snapshot kinds (`lib/identity.ts` in the UI kit) — decoration that
            always sits beside its word, never a status, so a Snapshot chip
            cannot be mistaken for a warning the way an amber one would be.
            The Blueprint also drew a "Static" chip on every other row; it is
            not drawn, for the slice 29 reason above, which
            `lib/segment-display.ts` states at length beside COHORT_CHIP.
          */
          const provenance = cohortProvenance(segment.cohort);

          return (
            <div className="min-w-0">
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <Link
                  to={`/segments/${segment.id}`}
                  className="min-w-0 truncate font-medium text-text underline-offset-4 hover:underline"
                >
                  {segment.name}
                </Link>
                {provenance !== null && <Badge tone="ident-a">{provenance.chip}</Badge>}
                {/* Slice 67: a rule segment moves by itself as data arrives, which is
                    the one fact about it a person sizing a send must not miss. */}
                {segment.type === 'rule' && (
                  <Badge tone="brand" dot>
                    Dynamic
                  </Badge>
                )}
              </span>
              {segment.description !== null && (
                <p className="truncate text-[0.82rem] text-muted" title={segment.description}>
                  {segment.description}
                </p>
              )}
              {provenance !== null && (
                <p className="truncate font-mono text-[0.7rem] tracking-[0.04em] text-muted">
                  {provenance.kind} · {provenance.countedAt}
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: 'lists',
        header: 'Lists',
        align: 'right',
        width: '7rem',
        cell: (segment) =>
          // A rule segment combines no uploads either; its "none" would read as
          // an empty container, so it says what defines it instead. Slice 67.
          segment.type === 'rule' ? (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-brand-ink">rules</span>
          ) : segment.datasetCount === 0 ? (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              none
            </span>
          ) : (
            <span className="text-[0.86rem] tabular-nums text-text">{segment.datasetCount}</span>
          ),
      },
      {
        key: 'people',
        header: 'People',
        align: 'right',
        width: '11rem',
        cell: (segment) => <ContactsAndRecipients counts={segment.counts} />,
      },
      {
        key: 'reach',
        // "Reachable", not "Reach", since slice 65 made the capitalised word the
        // paid-ads pillar's name. The legend under the table uses the same word.
        header: 'Reachable',
        width: '15rem',
        cell: (segment) => <ChannelReach counts={segment.counts} />,
      },
      {
        key: 'createdAt',
        header: 'Created',
        sortable: true,
        width: '10rem',
        cell: (segment) => (
          <span className="whitespace-nowrap text-muted">{formatDate(segment.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <AppLayout
      title="Segments"
      actions={
        <Link to="/segments/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          New segment
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        {failedToLoad ? (
          <Card>
            <ErrorState error={error} title="Could not load your segments" onRetry={reload} />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search by segment name"
                  aria-label="Search segments"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the segments" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'segment')}
                </p>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The segments below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Segments"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(segment) => segment.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="58rem"
              empty={search === '' ? <FirstSegment /> : <NoMatch search={search} />}
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />

            {(data?.items.length ?? 0) > 0 && <CountsLegend />}
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Explaining the columns                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Four numbers in two columns need a sentence, or the smaller ones read as
 * errors. It is here rather than in a tooltip because the difference between
 * them is the thing a person most needs to have understood before they send,
 * and a skimmer never opens a tooltip.
 *
 * **The Reach clause is a correction, not a rewrite.** It used to read "how
 * many of the rows carry an address of each kind", which is a row count — and
 * `emailReachable` and `phoneReachable` are counts of *distinct addresses*.
 * That is exactly the defect slice 2 shipped and paid for: a segment advertised
 * 531 where only 526 addresses were distinct, because reach had quietly become
 * an address count while the sentence describing it still said rows. A reader
 * who believes the old sentence is wrong by precisely the number that decides
 * how many messages go out, and they are wrong in the optimistic direction.
 * `segment-display.ts` and `SegmentCounts.tsx` have said "distinct addresses"
 * all along; this line is the one place on the path that disagreed with them.
 */
function CountsLegend() {
  return (
    <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
      <span className="text-muted">Contacts</span> is rows. <span className="text-muted">Unique</span>{' '}
      is how many different people those rows are, matched on email address or phone number — one
      message each, so it is the number that becomes messages.{' '}
      <span className="text-muted">Reachable</span> is distinct addresses of each kind, not rows carrying
      one; a campaign picks one channel and reaches only those.
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The first-run state. It says what a segment is *for*, because "no rows" in a
 * workspace that has never had one explains nothing — and because a segment is
 * the first idea in Wend that is not simply a thing you uploaded.
 *
 * It keeps two actions rather than one. The primary is the action this screen
 * is about; the secondary exists because the commonest reason to be standing
 * here with nothing is that there are no lists to combine yet, and a screen
 * whose only button leads somewhere you cannot use is a dead end.
 */
function FirstSegment() {
  return (
    <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
        <ListFilter aria-hidden="true" className="size-5" />
      </span>

      <div className="flex max-w-[34rem] flex-col gap-2">
        <h2 className="text-[1.1rem] font-semibold tracking-[-0.022em]">No segments yet</h2>
        <p className="text-[0.92rem] leading-relaxed text-muted">
          A segment is who you are going to send to — one or more of your uploaded lists, combined
          under a name you choose.
        </p>
        {/*
          SLICE 29. Two things in one sentence, and neither belongs on an empty
          screen. *Reference-not-copy* is an implementation fact, recurring
          verbatim at `NewSegmentPage`, twice on `SegmentDetailPage` and once in
          `SegmentCounts`. And the three counts are described to somebody who has
          no segment to count — they are shown, with numbers, the moment one
          exists.
        */}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link to="/segments/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          Create your first segment
        </Link>
        <Link
          to="/audiences/upload"
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <Upload aria-hidden="true" className="size-4" />
          Upload a list first
        </Link>
      </div>
    </CardContent>
  );
}

function NoMatch({ search }: { search: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <p className="text-[0.92rem] text-muted">
        No segment matches <span className="text-text">“{search}”</span>.
      </p>
      <p className="text-[0.85rem] text-muted">Searching matches the segment name.</p>
    </div>
  );
}
